import { FallOutlined, MoreOutlined, RiseOutlined, SwapRightOutlined, UserOutlined, WalletOutlined, WifiOutlined } from '@ant-design/icons';
import { Pagination, Select, DatePicker, ConfigProvider } from 'antd';
import { useData } from 'components/provider/data';
import { useTheme } from 'components/provider/theme';
import dayjs from 'dayjs';
import { API_URL } from 'env';
import { useState } from 'react';
import { useEffect } from 'react';
import { User } from 'pages/Dasboard/MainDashBoard/component/UserAccountList';
import { AccountInfo } from './AccountTable';
const { RangePicker } = DatePicker;
interface Transaction {
    accountName: string;
    login_id: string;
    platform: string;
    symbol: string;
    type: 'Buy' | 'Sell';
    quantity: number;
    price: string;
    total: string;
    pl: number;
    fee: string;
    dateTime: string;
    status: 'Completed' | 'Pending' | 'Failed';
}
const TransactionHistory = ({
    transactionData,
    option = {
        title: true,
        table: true,
        filter: true,
        pagination: true,
        randomize: true,
        border: true,
        shadow: true,
        padding: {
            top: '20px',
            bottom: '20px',
            left: '0',
            right: '0'
        },
        randomizeInterval: 1000
    }
}: {
    transactionData?: Transaction[],
    option?: {
        accountLoginId?: string;
        accountName?: string;
        platform?: string;
        title?: boolean;
        table?: boolean;
        filter?: boolean;
        pagination?: boolean;
        randomize?: boolean;
        randomizeInterval?: number;
        border?: boolean;
        shadow?: boolean;
        padding?: {
            top?: string;
            bottom?: string;
            left?: string;
            right?: string;
        }
        colWidth?: [
            string, string, string, string, string, string, string, string, string,
        ]
    }
}) => {
    const [data, setData] = useState<Transaction[]>();
    const [page, setPage] = useState(1);
    const { theme } = useTheme();
    const [reset, setReset] = useState(0);
    const { setTotalTrade, setTradeHistoryChartData, setAssignedAccountUser } = useData();
    const [closeTimeFrom, setCloseTimeFrom] = useState(dayjs().subtract(30, 'day').startOf('day').toISOString());
    async function fetchAvaliableAccounts() {
        try {
            // Get all accounts from the API
            const accounts = await fetch(`${API_URL}api/v1/be/trade_accounts`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!accounts.ok) {
                console.error('Failed to fetch accounts:', accounts.statusText);
                return [];
            }
            const accountsList = await accounts.json();
            const accountData = accountsList.data.items.map((item: any) => ({
                key: item.id,
                accountName: item.nickname,
                login_id: item.account_number,
                platform: item.platform,
                broker: item.broker,
            }));

            // Fetch transaction history for each account
            let transactionHistory: Transaction[] = [];
            for (const account of accountData) {
                const userResponse = await fetch(`https://fund-forge-api.wealthfarming.org/terminal-accounts?account_number=${account.login_id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                if (userResponse.ok) {
                    const tradeAccountRes = await fetch(
                        `https://fund-forge-api.wealthfarming.org/${account.platform.replace('4', '5')}/page/metrix?account_number=${account.login_id}&close_time_from=*&close_time_to=*`,
                        {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                        }
                    );
                    if (tradeAccountRes.ok) {
                        const accountdata = await tradeAccountRes.json();
                        const dataTrade: Transaction[] = accountdata.metrics.journal_closed_trades.map((trade: any) => ({
                            accountName: account.accountName,
                            login_id: account.login_id,
                            platform: account.platform,
                            symbol: trade.symbol,
                            type: String(trade.type).toLocaleLowerCase() === 'buy' ? 'Buy' : 'Sell',
                            quantity: trade.volume || 0,
                            price: trade.open_price || '0',
                            total: trade.close_price || '0',
                            pl: trade.PnL || 0,
                            fee: trade.commission || '0',
                            dateTime: trade.opentime,
                            status: "Completed",
                        }));
                        transactionHistory = transactionHistory.concat(dataTrade);
                        setData(transactionHistory.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()));
                        setTotalTrade(transactionHistory?.length || 0);

                        setTradeHistoryChartData((prev) => {
                            const filtered = prev.filter(item => item.label !== account.login_id);
                            return [
                                ...filtered,
                                {
                                    label: account.login_id,
                                    name: account.accountName,
                                    data: dataTrade.map((trade) => ({
                                        x: new Date(trade.dateTime),
                                        y: trade.pl,
                                    })),
                                }
                            ];
                        });


                    } else {
                        console.error('Failed to fetch user for account:', account.login_id);
                    }
                } else {
                    console.error('Failed to fetch user for account:', account.login_id);
                }
            }
            return;
        }
        catch (error) {
            console.error('Error fetching accounts:', error);
            return;
        }
    }

    async function fetchAccountTransactionHistory() {
        try {
            if (!option.accountLoginId || !option.platform) {
                console.error('No account login ID provided');
                return;
            }

            const response = await fetch(`https://fund-forge-api.wealthfarming.org/${option.platform.replace('4', '5')}/page/metrix?account_number=${option.accountLoginId}&close_time_from=${closeTimeFrom}&close_time_to=*`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!response.ok) {
                console.error('Failed to fetch transaction history:', response.statusText);
                return;
            }
            const data = await response.json();
            const transactionHistory: Transaction[] = data.metrics.journal_closed_trades.map((trade: any) => ({
                accountName: trade.account_name || 'N/A',
                login_id: option.accountLoginId,
                platform: option.platform,
                symbol: trade.symbol,
                type: String(trade.type).toLocaleLowerCase() === 'buy' ? 'Buy' : 'Sell',
                quantity: trade.volume || 0,
                price: trade.open_price || '0',
                total: trade.close_price || '0',
                pl: trade.PnL || 0,
                fee: trade.commission || '0',
                dateTime: trade.opentime,
                status: "Completed",
            }));
            setData(transactionHistory.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()));
            setTradeHistoryChartData((prev) => {
                const filtered = prev.filter(item => item.label !== (option.accountLoginId || 'N/A'));
                return [
                    ...filtered,
                    {
                        label: option.accountLoginId || 'N/A',
                        name: option.accountName || 'N/A',
                        data: transactionHistory.map((trade) => ({
                            x: new Date(trade.dateTime),
                            y: trade.pl,
                        })),
                    }
                ];
            });
            setTotalTrade(transactionHistory?.length || 0);
        } catch (error) {
            console.error('Error fetching transaction history:', error);
        }
    }
    // reset data every 1 second
    useEffect(() => {

        if (!option.accountLoginId) {
            fetchAvaliableAccounts()
        }
        else {
            fetchAccountTransactionHistory();
        }
    }, [closeTimeFrom]);
    return (
        <div className={`w-full ${theme === 'dark' ? 'bg-gray-800' : '!bg-white ring-1 ring-gray-100'} rounded-lg `} style={{
            border: option?.border ? '1px solid #e0e0e0' : 'none',
            boxShadow: option?.shadow ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
            padding: `${option?.padding?.top || '20px'} ${option?.padding?.right || '20px'} ${option?.padding?.bottom || '20px'} ${option?.padding?.left || '20px'}`,
        }}
        >
            {
                option?.title && (
                    <div className="flex items-center justify-between mb-4 mx-4">
                        <h6 className={`text-lg font-semibold m-0 ${theme === 'dark' ? 'text-white' : ''}`}>Transaction History</h6>
                        <p onClick={
                            () => {
                                setCloseTimeFrom('*');

                            }
                        } className="!text-blue-500 hover:underline mb-2 inline-block cursor-pointer">
                            View All Transactions
                            <SwapRightOutlined className="inline-block mr-1" />
                        </p>
                    </div>
                )
            }
            {
                option?.filter && (
                    <div className="flex items-center justify-start flex-row gap-3 mx-4 mb-4 ">
                        <Select
                            allowClear
                            variant='underlined'
                            placeholder="Symbol"
                            optionFilterProp="label"
                            onChange={
                                (value) => console.log(`Selected: ${value}`)
                            }
                            className="w-1/6 mb-4"
                            options={[
                                {
                                    value: 'eur_usd',
                                    label: 'EUR/USD',
                                },
                                {
                                    value: 'gbp_usd',
                                    label: 'GBP/USD',
                                },
                                {
                                    value: 'usd_jpy',
                                    label: 'USD/JPY',
                                },
                                {
                                    value: 'aud_cad',
                                    label: 'AUD/CAD',
                                },
                                {
                                    value: 'nzd_chf',
                                    label: 'NZD/CHF',
                                }
                            ]}
                        />
                        <Select
                            allowClear
                            variant='underlined'
                            placeholder="Type"
                            optionFilterProp="label"
                            onChange={
                                (value) => console.log(`Selected: ${value}`)
                            }
                            className="w-1/6 mb-4"
                            options={[
                                {
                                    value: 'buy',
                                    label: 'Buy',
                                },
                                {
                                    value: 'sell',
                                    label: 'Sell',
                                }
                            ]}
                        />
                        <ConfigProvider
                            theme={{
                                token: {
                                    colorText: "#fff",
                                },
                            }}
                        >
                            <RangePicker
                                variant="underlined"
                                className="w-3/6 mb-4 text-white"
                                onChange={(dates, dateStrings) => {
                                    console.log('Selected Dates:', dates);
                                    console.log('Formatted Dates:', dateStrings);
                                }}
                                showTime

                                placeholder={['Time Start', 'Time End']} />
                        </ConfigProvider>
                        <Select
                            allowClear
                            variant='underlined'
                            placeholder="Status"
                            optionFilterProp="label"
                            onChange={
                                (value) => console.log(`Selected: ${value}`)
                            }
                            className="w-1/6 mb-4"
                            options={[
                                {
                                    value: 'completed',
                                    label: 'Completed',
                                },
                                {
                                    value: 'pending',
                                    label: 'Pending',
                                },
                                {
                                    value: 'failed',
                                    label: 'Failed',
                                }
                            ]}
                        />
                    </div>
                )
            }
            {
                option?.table && (
                    <div className={`overflow-x-auto ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} `} style={{
                        scrollbarWidth: 'none',
                    }}>
                        <table className="w-full">
                            <thead>
                                <tr className={`${theme === 'dark' ? 'bg-gray-500 text-white' : 'bg-gray-100 border-gray-200'} text-[#6B7280]`}
                                    style={{
                                        borderBottom: theme === 'dark' ? '' : '1px solid #e0e0e0',
                                        borderTop: theme === 'dark' ? '' : '1px solid #e0e0e0'
                                    }}
                                >
                                    <th className="text-left p-2 font-light">Account</th>
                                    <th className="text-left p-2 font-light">Platform</th>
                                    <th className="text-left p-2 font-light">Symbol</th>
                                    <th className="text-left p-2 font-light">Type</th>
                                    <th className="text-left p-2 font-light">Quantity</th>
                                    {/* <th className="text-left p-2 font-light">Price</th> */}
                                    {/* <th className="text-left p-2 font-light">Total</th> */}
                                    <th className="text-left p-2 font-light">P&amp;L</th>
                                    <th className="text-left p-2 font-light">Date Time</th>
                                    {/* <th className="text-left p-2 font-light">Status</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    data?.slice((page - 1) * 10, page * 10).map((item, index) => (
                                        <tr key={index} className={` ${theme === 'dark' ? 'border-b border-gray-700 hover:bg-gray-700' : 'border-b border-gray-200 hover:bg-gray-50'} w-full !h-12 !px-8`}>
                                            <td className={`p-2 w-[10%] text-sm ${option?.colWidth ? option.colWidth[0] : ''}`}>
                                                <div className="flex items-center gap-2">
                                                    <UserOutlined className="text-gray-500" />
                                                    <span>{option.accountName ? option.accountName : item.accountName}</span>
                                                </div>
                                            </td>
                                            <td className={`p-2 w-[10%] text-sm ${option?.colWidth ? option.colWidth[1] : ''}`}>
                                                <div className="flex items-center gap-2 justify-between">
                                                    <span className={` px-2 py-1 rounded-[6px] ${item.platform.includes('mt') ? ' bg-blue-300 text-blue-800' : 'bg-orange-300 text-orange-700'}`}>{item.platform}</span>
                                                    <span>#{item.login_id}</span>
                                                </div>
                                            </td>
                                            <td className={`p-2 w-[10%] text-sm ${option?.colWidth ? option.colWidth[2] : ''}`}>
                                                {item.symbol}
                                            </td>
                                            <td className={`p-2 w-[10%] ${option?.colWidth ? option.colWidth[3] : ''} ${item.type === 'Buy' ? 'text-green-500' : 'text-red-500'}`}>
                                                {item.type}
                                            </td>
                                            <td className={`p-2 w-[10%] ${option?.colWidth ? option.colWidth[4] : ''}`}>
                                                {item.quantity}
                                            </td>
                                            {/* <td className={`p-2 w-[10%] ${option?.colWidth ? option.colWidth[3] : ''}`}>
                                                ${item.price}
                                            </td>
                                            <td className={`p-2 w-[10%] ${option?.colWidth ? option.colWidth[4] : ''}`}>
                                                ${item.total}
                                            </td> */}
                                            <td className={`p-2 w-[10%] 
                                                ${option?.colWidth ? option.colWidth[5] : ''}
                                                ${(item.pl > 0) ? 'text-green-500' : (item.pl < 0) ? 'text-red-500' : 'text-yellow-500'}`}>
                                                {item.pl > 0 ? '+' : ''}${item.pl.toFixed(2)}
                                            </td>
                                            <td className={`p-2 w-[10%] ${option?.colWidth ? option.colWidth[6] : ''}`}>
                                                {dayjs(item.dateTime).format('YYYY-MM-DD HH:mm:ss')}
                                            </td>
                                            {/* <td className={`p-2 w-[10%] ${option?.colWidth ? option.colWidth[8] : ''}`}>
                                                <div
                                                    className={`w-full items-center text-center px-2 py-1 rounded-full text-xs font-semibold
                                                    ${item.status === 'Completed'
                                                            ? 'bg-green-100 text-green-800'
                                                            : item.status === 'Pending'
                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                : 'bg-red-100 text-red-800'
                                                        }`}
                                                >
                                                    {item.status === 'Completed' ? (
                                                        <span className="text-green-500 font-semibold">Completed</span>
                                                    ) : item.status === 'Pending' ? (
                                                        <span className="text-yellow-500 font-semibold">Pending</span>
                                                    ) : (
                                                        <span className="text-red-500 font-semibold">Failed</span>
                                                    )}
                                                </div>
                                            </td> */}
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                        {
                            option?.pagination && (
                                <div className="mt-4 flex justify-end px-4">
                                    <Pagination
                                        defaultCurrent={1}
                                        total={data?.length}
                                        pageSize={10}
                                        showSizeChanger={false}
                                        onChange={(page, pageSize) => {
                                            setPage(page);
                                        }}
                                    />
                                </div>
                            )
                        }
                    </div>
                )
            }
        </div>
    )
}

export default TransactionHistory;
export type { Transaction };